import React from 'react';
import { 
  Box, 
  Container, 
  Typography, 
  Grid,
  Link
} from '@mui/material';
import styled from '@emotion/styled';

// Styled components
const ServicesSection = styled(Box)(({ theme }) => ({
  backgroundColor: '#101010',
  padding: '100px 0',
  position: 'relative',
  backgroundImage: 'url("/assets/images/bg/ser-bg.png")',
  backgroundPosition: 'center',
  backgroundRepeat: 'no-repeat',
  backgroundSize: 'cover',
  [theme.breakpoints.down('md')]: {
    padding: '70px 0',
  }
}));

const SectionTitle = styled(Box)(({ theme }) => ({
  marginBottom: '70px',
  [theme.breakpoints.down('md')]: {
    marginBottom: '30px',
  }
}));

const SectionHeader = styled(Grid)(({ theme }) => ({
  marginBottom: '30px',
  [theme.breakpoints.down('md')]: {
    marginBottom: '30px',
  }
}));

const ViewButton = styled(Link)(({ theme }) => ({
  display: 'inline-block',
  backgroundColor: '#bb0b0b',
  color: '#ffffff',
  padding: '12px 30px',
  borderRadius: '0',
  fontSize: '16px',
  fontWeight: 600,
  textDecoration: 'none',
  textTransform: 'uppercase',
  transition: 'all 0.3s ease',
  '&:hover': {
    backgroundColor: '#b00824',
    textDecoration: 'none',
  },
  [theme.breakpoints.down('md')]: {
    marginTop: '20px',
  }
}));

const Title = styled(Typography)(({ theme }) => ({
  color: '#ffffff',
  position: 'relative',
  fontWeight: 700,
  fontSize: '36px',
  lineHeight: 1.3,
  marginBottom: '30px',
  paddingTop: '30px',
  '&:before': {
    content: '""',
    position: 'absolute',
    left: 0,
    top: 0,
    display: 'block',
    width: '70px',
    height: '4px',
    backgroundColor: '#d90a2c',
  }
}));

const ServiceItem = styled(Box)(({ theme }) => ({
  position: 'relative',
  marginBottom: '30px',
  transition: 'all 0.3s ease',
  display: 'flex',
  flexDirection: 'row',
  alignItems: 'flex-start',
  '&:hover': {
    transform: 'translateY(-5px)',
  }
}));

const ServiceIcon = styled(Box)(({ theme }) => ({
  marginRight: '15px',
  flexShrink: 0,
  '& img': {
    height: 'auto',
    width: 'auto',
    maxHeight: '40px',
    maxWidth: '40px',
  }
}));

const ServiceTitle = styled(Typography)(({ theme }) => ({
  fontSize: '18px',
  fontWeight: 800,
  marginBottom: '10px',
  color: '#ffffff',
  lineHeight: 1.7,
  '& a': {
    color: '#ffffff',
    textDecoration: 'none',
    transition: 'all 0.3s ease',
    '&:hover': {
      color: '#d90a2c',
    }
  }
}));

const ServiceText = styled(Typography)(({ theme }) => ({
  color: '#ffffff',
  fontSize: '16px',
  lineHeight: 1.7
}));

function Services() {
  const services = [
    {
      id: 1,
      icon: '/assets/images/services/main-home/1.png',
      title: 'Cyber Uplift Planning',
      description: 'Enhancing your cybersecurity with strategic foresight, all within your budget for Unparalleled Cyber Resilience',
      link: 'business-planning.html'
    },
    {
      id: 2,
      icon: '/assets/images/services/main-home/5.png',
      title: 'Secure Software Development',
      description: 'Empower your innovation with Secure Software Development, ensuring your applications are as resilient as they are revolutionary',
      link: 'start-ups.html'
    },
    {
      id: 3,
      icon: '/assets/images/services/main-home/2.png',
      title: 'Security Architecture',
      description: 'By utilizing our architecture services, you can be confident that your cloud-stored data is secure, compliant, and protected against cyber threats',
      link: 'tax-strategy.html'
    },
    {
      id: 4,
      icon: '/assets/images/services/main-home/4.png',
      title: 'SecOps Automation',
      description: 'We leverage the latest advancements in artificial intelligence, machine learning, and automation to streamline and accelerate security operations',
      link: 'insurance-strategy.html'
    },
    {
      id: 5,
      icon: '/assets/images/services/main-home/6.png',
      title: 'DevSecOps for Cloud Native',
      description: 'Seamlessly integrating security into your cloud-native development lifecycle, our DevSecOps service ensures every build is secure by design, from inception to deployment.',
      link: 'manage-investment.html'
    },
    {
      id: 6,
      icon: '/assets/images/services/main-home/3.png',
      title: 'Managed GRC',
      description: 'Navigate the complexities of business with precision through our Governance, Risk Management, and Compliance services, designed to elevate your operations with robust security and unwavering adherence to regulations',
      link: 'financial-advices.html'
    }
  ];

  return (
    <ServicesSection id="rs-services" className="rs-services main-home bg2 pt-100 pb-100 md-pt-70 md-pb-70">
      <Container maxWidth="lg">
        <SectionHeader container>
          <Grid item xs={12} md={7}>
            <SectionTitle>
              <Title variant="h2">
                What services we provide to our customers business
              </Title>
            </SectionTitle>
          </Grid>
          <Grid item xs={12} md={5} sx={{ 
            textAlign: { md: 'right' }, 
            [theme => theme.breakpoints.down('md')]: { textAlign: 'left' }
          }}>
            <Box sx={{ marginTop: { xs: '20px', md: 0 } }}>
              <ViewButton href="#rs-services">
                View Services
              </ViewButton>
            </Box>
          </Grid>
        </SectionHeader>

        <Grid container spacing={4}>
          {services.map((service, index) => {
            
            return (
              <Grid item xs={12} sm={6} md={4} key={service.id}>
                <ServiceItem>
                  <ServiceIcon>
                    <img src={service.icon} alt={`${service.title} Services`} />
                  </ServiceIcon>
                  <Box>
                    <ServiceTitle variant="h6">
                      <Link href={service.link}>{service.title}</Link>
                    </ServiceTitle>
                    <ServiceText>
                      {service.description}
                    </ServiceText>
                  </Box>
                </ServiceItem>
              </Grid>
            );
          })}
        </Grid>
      </Container>
    </ServicesSection>
  );
}

export default Services;
